import { useEffect, useMemo } from 'react';

import { ChatUser } from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import ChannelTile from '~/components/maud/patients/profile/tabs/patientMessagesTab/ChannelTile';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useSendbirdReadyContext } from '~/lib/contexts/SendbirdReadyContext';
import useChat from '~/lib/hooks/chat/useChat';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import {
  getAdminMessageText,
  getLastChannelMessage,
  getLastChannelMessageRelativeDate,
  isExternalChannel,
} from '~/lib/util/chat/chat.utils';
import { getChannelName } from '~/lib/util/chat/chatChannel.util';
import { Path } from '~/routes/constants';

import { RailSection } from './RailSection';
import { RailSectionErrorLabel } from './rightRail.constants';
import { getTotalUnread, sortUnreadChannelsByRecent } from './rightRail.util';

interface MessagesSectionProps {
  patientId: string;
  universalStatusCode?: string;
}

const MessagesSection = ({
  patientId,
  universalStatusCode,
}: MessagesSectionProps) => {
  const history = useHistory();
  const { currentUser } = useGlobalContext();
  const { sdk } = useSendbirdReadyContext();

  const { channels, isChannelsError, isChannelsLoading, loadChannels } =
    useChat({
      channel: null,
      chatUserId: currentUser.userId,
      onChannelUpdated: () => undefined,
      patientId,
      universalStatusCode,
    });

  // loadChannels is unmemoized, so listing it as a dep would infinite-loop.
  useEffect(() => {
    if (sdk && patientId) {
      loadChannels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk, patientId]);

  const unreadChannels = useMemo(
    () => sortUnreadChannelsByRecent(channels ?? []),
    [channels],
  );
  const totalUnread = useMemo(() => getTotalUnread(channels ?? []), [channels]);

  const goToChannel = (channelUrl: string) =>
    history.push(
      `${Path.MAUD_PATIENTS}/${patientId}/${
        PatientTabsCollection[PatientTabs.MESSAGES].subPath
      }?channelUrl=${channelUrl}`,
    );

  const rows = unreadChannels.map((channel) => (
    <ChannelTile
      channelMembers={channel.members as ChatUser[]}
      channelTitle={getChannelName(channel)}
      hasUnreadMessages
      id={`overview-msg__${channel.url}`}
      isExternalChannel={isExternalChannel(channel.url)}
      isFlat
      isLastFile={channel.lastMessage?.isFileMessage?.() ?? false}
      isSelected={false}
      key={channel.url}
      lastMessage={getAdminMessageText(
        getLastChannelMessage(channel.lastMessage),
        '',
      )}
      lastMessageRelativeDate={getLastChannelMessageRelativeDate(
        channel.lastMessage,
      )}
      onClick={() => goToChannel(channel.url)}
    />
  ));

  return (
    <RailSection
      action={{
        linkTo: PatientTabsCollection[PatientTabs.MESSAGES].subPath,
        patientId,
        rightLinkText: 'View all',
      }}
      badge={{
        count: totalUnread,
        counterId: 'msg-unread-count',
        description: 'unread',
        type: 'counter',
      }}
      emptyCaption="No unread messages"
      errorLabel={RailSectionErrorLabel.Messages}
      headerText="Messages"
      id="patient-overview-right-rail-v2__messages"
      isError={isChannelsError}
      isLoading={isChannelsLoading}
      rows={rows}
    />
  );
};

export default MessagesSection;
